import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import logo from "@/assets/logo.png";

const links = [
  { label: "Vision", href: "#vision" },
  { label: "Sermons", href: "#sermons" },
  { label: "Events", href: "#events" },
  { label: "Turnaround", href: "#turnaround" },
  { label: "Vendors", href: "#vendor" },
  { label: "Support", href: "#support" },
  { label: "Membership", href: "#membership" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8 }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-[#06040A]/90 backdrop-blur-xl border-b border-[#D4AF37]/20 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 flex items-center justify-between">
        <a href="#top" data-testid="header-logo-link" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          <img src={logo} alt="The Change Center Church" className="h-11 w-11 object-cover rounded-lg border border-[#D4AF37]/40" />
          <div className="hidden sm:block leading-tight">
            <p className="font-display text-lg gold-text font-semibold tracking-wide">The Change Center</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#A19CA8]">Church · Atlanta</p>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm uppercase tracking-[0.16em] text-[#A19CA8] hover:text-[#D4AF37] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href="#vendor"
          data-testid="header-cta"
          className="hidden lg:inline-flex bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#06040A] font-semibold uppercase tracking-wider text-xs px-6 py-3 hover:from-[#F3D56D] hover:to-[#D4AF37] transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)]"
        >
          Register
        </a>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden text-[#D4AF37] p-1"
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-nav"
            className="lg:hidden overflow-hidden bg-[#06040A]/98 border-t border-[#D4AF37]/15"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="py-3 text-base uppercase tracking-[0.16em] text-[#F9F6F0] border-b border-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#vendor"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta"
                className="mt-4 text-center bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#06040A] font-semibold uppercase tracking-wider text-sm px-6 py-4"
              >
                Register for Events
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
